import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api, formatDateTime, mensajeError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { puedeMarcarProhibido } from '../../lib/roles';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { COLOR_CAUTION, COLOR_SEGUIMIENTO, IndicadorCaution, IndicadorSeguimiento } from '../IndicadoresSeguimiento';

/**
 * BOTONES «SEGUIMIENTO» Y «CAUTION» EN LA FICHA (administración, 4/11/2026)
 * ------------------------------------------------------------------------
 * Junto a «Paciente prohibido» y las marcas de paciente, en otros colores
 * (índigo y ámbar: ni el rosa de las marcas ni el rojo de PROHIBIDO).
 *
 *  · «Seguimiento» (sin colorear) / «En seguimiento» (índigo): abre la nota
 *    del seguimiento y a quién se asigna (el equipo de secretaría, que sale de
 *    Usuarios: GET /api/seguimientos/equipo). Cada «Guardar» crea un episodio
 *    con la fecha y hora del servidor, que se ve en el apartado «Seguimientos»
 *    de la ficha. Con el seguimiento activo, «Seguimiento completado».
 *  · «Caution» (borde ámbar) / «CAUTION» (ámbar): marca de precaución con su
 *    nota; activa, se ve la nota, se puede cambiar y quitar (con confirmación).
 *
 * Escriben los mismos que «Paciente prohibido» (`puedeMarcarProhibido`); el
 * resto ve solo los indicadores. El servidor lo vuelve a comprobar.
 */

const LARGO_MAXIMO_NOTA = 4000;  // services/seguimiento_paciente.LARGO_MAXIMO_NOTA

function DialogoSeguimiento({ abierto, onCerrar, paciente, onGuardado }) {
  const seguimiento = paciente?.seguimiento || {};
  const activo = seguimiento.estado === 'en_seguimiento';
  const [nota, setNota] = useState('');
  const [asignado, setAsignado] = useState('');
  // Solo se manda la asignación si se ha tocado el desplegable: sin tocarlo,
  // el servidor deja a quien ya tenía (también si ya no está en el equipo o
  // la lista no se ha podido leer). «Sin asignar» elegido a mano la quita.
  const [asignadoTocado, setAsignadoTocado] = useState(false);
  const [equipo, setEquipo] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [confirmarCompletar, setConfirmarCompletar] = useState(false);

  useEffect(() => {
    if (!abierto) return undefined;
    setNota('');
    setAsignado('');
    setAsignadoTocado(false);
    setConfirmarCompletar(false);
    let vivo = true;
    api.get('/api/seguimientos/equipo')
      .then((r) => {
        if (!vivo) return;
        const lista = r.data?.equipo || [];
        setEquipo(lista);
        // Empieza en la persona que ya lo tiene solo si sigue en el equipo;
        // si no, en «Sin asignar» (nunca un id que el servidor rechazaría).
        const actual = seguimiento.asignado_a_id;
        setAsignado(actual && lista.some((m) => m.id === actual) ? actual : '');
      })
      .catch(() => { if (vivo) { setEquipo([]); setAsignado(''); } });
    return () => { vivo = false; };
  }, [abierto, seguimiento.asignado_a_id]);

  const guardar = async () => {
    if (!nota.trim()) return;
    setGuardando(true);
    try {
      const cuerpo = { nota: nota.trim() };
      if (asignadoTocado) cuerpo.asignado_a_id = asignado || null;
      const { data } = await api.post(`/api/seguimientos/pacientes/${paciente.id}/episodios`, cuerpo);
      toast.success(seguimiento.estado === 'completado'
        ? 'Seguimiento reactivado y nota guardada'
        : 'Nota de seguimiento guardada');
      onGuardado?.(data);
      onCerrar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar el seguimiento'));
    } finally {
      setGuardando(false);
    }
  };

  const completar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.post(`/api/seguimientos/pacientes/${paciente.id}/completar`);
      toast.success('Seguimiento completado');
      onGuardado?.(data);
      onCerrar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido completar el seguimiento'));
    } finally {
      setGuardando(false);
    }
  };

  // Si la persona asignada ya no está en el equipo (baja), se dice debajo.
  const asignadoFuera = seguimiento.asignado_a_id && !equipo.some((m) => m.id === seguimiento.asignado_a_id);

  return (
    <Dialog open={abierto} onOpenChange={(a) => { if (!a) onCerrar(); }}>
      <DialogContent className="max-w-lg" data-testid="seguimiento-dialogo">
        <DialogHeader>
          <DialogTitle>{activo ? 'En seguimiento' : 'Seguimiento'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {activo && (
            <p className="text-muted-foreground" data-testid="seguimiento-estado">
              En seguimiento desde {formatDateTime(seguimiento.desde)}
              {seguimiento.asignado_a_nombre ? ` · asignado a ${seguimiento.asignado_a_nombre}` : ''}
            </p>
          )}
          {seguimiento.estado === 'completado' && (
            <p className="text-muted-foreground" data-testid="seguimiento-estado">
              Seguimiento completado el {formatDateTime(seguimiento.completado_at)}
              {seguimiento.completado_por_nombre ? ` por ${seguimiento.completado_por_nombre}` : ''}.
              Una nota nueva lo vuelve a poner en seguimiento.
            </p>
          )}
          <div>
            <Label htmlFor="seguimiento-nota">Nota del seguimiento <span className="text-red-600">*</span></Label>
            <Textarea
              id="seguimiento-nota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={4}
              maxLength={LARGO_MAXIMO_NOTA}
              placeholder="Qué se ha hecho o qué queda pendiente"
              className="mt-1.5"
              data-testid="seguimiento-nota"
            />
            <p className="text-[11px] text-muted-foreground mt-1">La fecha y la hora se guardan solas.</p>
          </div>
          <div>
            <Label htmlFor="seguimiento-asignado">Asignado a</Label>
            <select
              id="seguimiento-asignado"
              value={asignado}
              onChange={(e) => { setAsignado(e.target.value); setAsignadoTocado(true); }}
              className="mt-1.5 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="seguimiento-asignado"
            >
              <option value="">Sin asignar</option>
              {equipo.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </select>
            {asignadoFuera && !asignadoTocado && (
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="seguimiento-asignado-fuera">
                Ahora está asignado a {seguimiento.asignado_a_nombre || 'una persona'}, que ya no está en el
                equipo. Si no eliges a nadie, se queda como está.
              </p>
            )}
          </div>
          {confirmarCompletar && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="seguimiento-confirmar-completar">
              ¿Marcar el seguimiento como completado? Pasa a «Seguimiento completado» y las notas se
              quedan en la ficha. Una nota nueva lo vuelve a poner en seguimiento.
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
          {activo && (confirmarCompletar ? (
            <Button type="button" onClick={completar} disabled={guardando}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white"
              data-testid="seguimiento-completar-confirmar">
              Sí, completado
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => setConfirmarCompletar(true)} disabled={guardando}
              data-testid="seguimiento-completar">
              Seguimiento completado
            </Button>
          ))}
          <Button type="button" variant="outline" onClick={onCerrar}>Cancelar</Button>
          <Button
            type="button"
            onClick={guardar}
            disabled={guardando || !nota.trim()}
            className="text-white"
            style={{ backgroundColor: COLOR_SEGUIMIENTO }}
            data-testid="seguimiento-guardar"
          >
            {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Guardar nota
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogoCaution({ abierto, onCerrar, paciente, onGuardado }) {
  const activa = paciente?.caution === true;
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);

  useEffect(() => {
    if (abierto) {
      setNota(activa ? (paciente?.caution_nota || '') : '');
      setConfirmarQuitar(false);
    }
  }, [abierto, activa, paciente?.caution_nota]);

  const ruta = `/api/caution/pacientes/${paciente?.id}`;
  const sinCambios = activa && nota.trim() === String(paciente?.caution_nota || '').trim();

  const guardar = async () => {
    if (!nota.trim() || sinCambios) return;
    setGuardando(true);
    try {
      const { data } = activa
        ? await api.put(ruta, { nota: nota.trim() })
        : await api.post(ruta, { nota: nota.trim() });
      toast.success(activa ? 'Nota de Caution cambiada' : 'Paciente marcado con Caution');
      onGuardado?.(data);
      onCerrar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar Caution'));
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.delete(ruta);
      toast.success('Caution quitado');
      onGuardado?.(data);
      onCerrar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido quitar Caution'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={abierto} onOpenChange={(a) => { if (!a) onCerrar(); }}>
      <DialogContent className="max-w-lg" data-testid="caution-dialogo">
        <DialogHeader>
          <DialogTitle>{activa ? 'Caution (precaución)' : 'Marcar con Caution'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {activa && (
            <p className="text-muted-foreground" data-testid="caution-quien">
              Marcado el {formatDateTime(paciente?.caution_at)}
              {paciente?.caution_por_nombre ? ` por ${paciente.caution_por_nombre}` : ''}.
            </p>
          )}
          <div>
            <Label htmlFor="caution-nota">Motivo de la precaución <span className="text-red-600">*</span></Label>
            <Textarea
              id="caution-nota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={4}
              maxLength={LARGO_MAXIMO_NOTA}
              placeholder="Explica por qué hay que tener precaución con este paciente (uso interno)"
              className="mt-1.5"
              data-testid="caution-nota"
            />
          </div>
          {confirmarQuitar && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="caution-confirmar-quitar">
              ¿Quitar la señal de precaución? En el historial queda quién la quita, cuándo y la nota que tenía.
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
          {activa && (confirmarQuitar ? (
            <Button type="button" onClick={quitar} disabled={guardando}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white"
              data-testid="caution-quitar-confirmar">
              Sí, quitar Caution
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => setConfirmarQuitar(true)} disabled={guardando}
              data-testid="caution-quitar">
              Quitar Caution
            </Button>
          ))}
          <Button type="button" variant="outline" onClick={onCerrar}>Cancelar</Button>
          <Button
            type="button"
            onClick={guardar}
            disabled={guardando || !nota.trim() || sinCambios}
            className="text-white"
            style={{ backgroundColor: COLOR_CAUTION }}
            data-testid="caution-guardar"
          >
            {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {activa ? 'Guardar nota' : 'Marcar Caution'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BotonesSeguimientoCaution({ paciente, onCambio }) {
  const { user } = useAuth();
  const puede = puedeMarcarProhibido(user);
  const [abierto, setAbierto] = useState(null); // 'seguimiento' | 'caution'
  if (!paciente?.id) return null;
  const enSeguimiento = paciente.seguimiento?.estado === 'en_seguimiento';
  const caution = paciente.caution === true;

  if (!puede) {
    if (!enSeguimiento && !caution) return null;
    return (
      <span className="inline-flex flex-wrap items-center gap-2 align-middle font-sans text-base font-normal" data-testid="seguimiento-caution">
        {enSeguimiento && <IndicadorSeguimiento testId="ficha-en-seguimiento" />}
        {caution && <IndicadorCaution nota={paciente.caution_nota} testId="ficha-caution" />}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2 align-middle font-sans text-base font-normal" data-testid="seguimiento-caution">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setAbierto('seguimiento')}
        aria-pressed={enSeguimiento ? 'true' : 'false'}
        className={`h-7 px-2 text-xs font-semibold ${enSeguimiento ? 'text-white border-transparent hover:opacity-90 hover:text-white' : 'text-muted-foreground'}`}
        style={enSeguimiento ? { backgroundColor: COLOR_SEGUIMIENTO } : undefined}
        title={enSeguimiento ? 'Paciente en seguimiento: añadir nota o completarlo' : 'Abrir un seguimiento con una nota'}
        data-testid="boton-seguimiento"
      >
        {enSeguimiento ? 'En seguimiento' : 'Seguimiento'}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setAbierto('caution')}
        aria-pressed={caution ? 'true' : 'false'}
        className={`h-7 px-2 text-xs font-semibold ${caution ? 'text-white border-transparent uppercase hover:opacity-90 hover:text-white' : ''}`}
        style={caution ? { backgroundColor: COLOR_CAUTION } : { borderColor: COLOR_CAUTION, color: COLOR_CAUTION }}
        title={caution ? `Caution: ${paciente.caution_nota || ''}` : 'Marcar al paciente con una señal de precaución'}
        data-testid="boton-caution"
      >
        Caution
      </Button>
      <DialogoSeguimiento
        abierto={abierto === 'seguimiento'}
        onCerrar={() => setAbierto(null)}
        paciente={paciente}
        onGuardado={onCambio}
      />
      <DialogoCaution
        abierto={abierto === 'caution'}
        onCerrar={() => setAbierto(null)}
        paciente={paciente}
        onGuardado={onCambio}
      />
    </span>
  );
}
